import { t } from '../../../../services/HelperService';
import { buildAliasFieldTemplateGroup } from '../../templates/buildWorkflowTemplateCatalog';

const safeString = (v) => (v ?? '').toString().trim();

/**
 * Builds the JavaScript snippet catalog for trigger output expressions: the host JS
 * catalog when provided, otherwise the workflow catalog extended with the built-in
 * trigger outputs, the trigger entity fields, and the entity field catalog.
 */
export const buildTriggerOutputJsCatalog = ({
  metadata,
  triggerEntityType,
  templateCatalog,
  javascriptTemplateCatalog,
  entityFieldCatalog,
  builtInTriggerOutputs,
}) => {
  const hasJsCatalog = Array.isArray(javascriptTemplateCatalog) && javascriptTemplateCatalog.length > 0;
  const base = hasJsCatalog ? javascriptTemplateCatalog : (Array.isArray(templateCatalog) ? templateCatalog : []);
  const extra = [];

  const builtInItems = (Array.isArray(builtInTriggerOutputs) ? builtInTriggerOutputs : [])
    .map((item) => ({
      token: safeString(item?.token),
      snippet: safeString(item?.token).replace(/^\{\{\s*|\s*\}\}$/g, ''),
      label: safeString(item?.label),
    }))
    .filter((item) => !!item.token);

  if (!hasJsCatalog && builtInItems.length > 0) {
    extra.push({
      group: t('Trigger'),
      items: builtInItems,
    });
  }

  const triggerEntityGroup = buildAliasFieldTemplateGroup(metadata, triggerEntityType, 'ctx.triggerEntity', t('Trigger Entity'));
  if (!hasJsCatalog && triggerEntityGroup) {
    extra.push(triggerEntityGroup);
  }

  if (entityFieldCatalog) {
    extra.push(entityFieldCatalog);
  }

  return [...base, ...extra];
};

export const getBuiltInTriggerOutputs = (subtype) => {
  const safeSubtype = safeString(subtype);
  if (safeSubtype === 'EntityTimer') {
    return [
      { token: '{{trigger.entityType}}', label: t('Workflow entity type key') },
      { token: '{{trigger.entityId}}', label: t('Workflow entity ID from the timer query') },
      { token: '{{trigger.slotKey}}', label: t('Deterministic schedule slot key') },
      { token: '{{trigger.scheduledAtUtc}}', label: t('Scheduled slot time in UTC') },
    ];
  }
  if (safeSubtype === 'BatchTimer') {
    return [
      { token: '{{trigger.type}}', label: t('Trigger type key') },
      { token: '{{trigger.slotKey}}', label: t('Deterministic schedule slot key') },
      { token: '{{trigger.scheduledAtUtc}}', label: t('Scheduled slot time in UTC') },
      { token: '{{trigger.rows}}', label: t('Batch query rows available while evaluating this trigger') },
      { token: '{{trigger.count}}', label: t('Batch query row count available while evaluating this trigger') },
    ];
  }
  if (safeSubtype === 'BasicTimer') {
    return [
      { token: '{{trigger.type}}', label: t('Trigger type key') },
      { token: '{{trigger.slotKey}}', label: t('Deterministic schedule slot key') },
      { token: '{{trigger.scheduledAtUtc}}', label: t('Scheduled slot time in UTC') },
    ];
  }
  if (safeSubtype === 'WorkflowCall') {
    return [
      { token: '{{trigger.type}}', label: t('Trigger type key') },
      { token: '{{trigger.entityType}}', label: t('Entity type key passed by the calling workflow (may be empty)') },
      { token: '{{trigger.entityId}}', label: t('Entity ID passed by the calling workflow (may be empty)') },
      { token: '{{trigger.parentRunId}}', label: t('Run ID of the calling (parent) workflow') },
      { token: '{{trigger.parentWorkflowKey}}', label: t('Workflow key of the calling (parent) workflow') },
      { token: '{{trigger.item}}', label: t('The array element this child was started for (items mode; ctx.trigger.item in scripts)') },
      { token: '{{trigger.itemIndex}}', label: t('Position of this child\u2019s element in the parent\u2019s array (items mode)') },
      { token: '{{trigger.itemCount}}', label: t('Number of elements the parent fanned out (items mode)') },
    ];
  }
  if (safeSubtype === 'EntityFieldChanged' || safeSubtype === 'EntityFieldChangedToValue') {
    return [
      { token: '{{trigger.entityType}}', label: t('Entity type key') },
      { token: '{{trigger.entityId}}', label: t('Entity ID') },
      { token: '{{trigger.fieldKey}}', label: t('Field key') },
      { token: '{{trigger.oldValue}}', label: t('Old value') },
      { token: '{{trigger.newValue}}', label: t('New value') },
    ];
  }

  return [
    { token: '{{trigger.entityType}}', label: t('Entity type key') },
    { token: '{{trigger.entityId}}', label: t('Created entity ID') },
  ];
};

export const getDefaultTriggerOutputVariable = () => ({
  name: '',
  valueMode: 'javascript',
  valueTemplate: '',
  script: '',
});

export const getTriggerOutputVariables = (config) => {
  const rows = Array.isArray(config?.triggerOutputVariables) ? config.triggerOutputVariables : [];
  return rows.map((row) => ({
    name: safeString(row?.name),
    valueMode: safeString(row?.valueMode) || 'template',
    valueTemplate: (row?.valueTemplate ?? '').toString(),
    script: (row?.script ?? '').toString(),
  }));
};

export const getTriggerOutputVariableItems = (config) => {
  return getTriggerOutputVariables(config)
    .filter((row) => !!row.name)
    .map((row) => ({
      token: `{{vars.${row.name}}}`,
      snippet: `vars.${row.name}`,
      label: `{{vars.${row.name}}}`,
    }));
};

export const getTriggerOutputVariableRows = (config) => {
  return getTriggerOutputVariables(config)
    .filter((row) => !!row.name)
    .map((row) => ({
      key: `{{vars.${row.name}}}`,
      availability: t('Custom trigger variable computed after the trigger guard passes.'),
    }));
};